import { createSelector } from '@reduxjs/toolkit';
import { getPath } from 'Utils/primitives';
import store from 'Features/App/store';
import { adapterForApi, createAdapterFilter } from 'Utils/rtkq.utils';
import { toaster } from 'evergreen-ui';
import { dcpcApi, invalidateTags } from './dcpcApi';

export const STATIONS_INVALIDATE_TAG = 'STATIONS';
const STATION_CHANNELS_INVALIDATE_TAG = 'STATION_CHANNELS';

dcpcApi.enhanceEndpoints({ addTagTypes: [STATIONS_INVALIDATE_TAG, STATION_CHANNELS_INVALIDATE_TAG] });
const stationsApi = dcpcApi.injectEndpoints({
  endpoints: (builder) => ({
    // Получение станций
    getStations: builder.query({ // TODO: выпилить
      query: () => ({
        url: 'stations-datasources',
        method: 'get',
      }),
      providesTags: [STATIONS_INVALIDATE_TAG],
    }),

    getDatasourceByStation: builder.query({
      query: (id, body) => ({
        url: `stations/${id}/data-sources`,
        method: 'GET',
        body,
      }),
      providesTags: [STATIONS_INVALIDATE_TAG],
    }),

    // updateStation: builder.mutation({
    //   query: (station) => ({
    //     method: station.id ? 'PUT' : 'POST',
    //     url: `station${station.id ? `/${station.id}` : ''}`,
    //     body: station,
    //   }),
    //   // dont need 'invalidatesTags' because  WS send 'reload' on update stations
    //   // invalidatesTags: [STATIONS_INVALIDATE_TAG],
    // }),

    deleteStation: builder.mutation({
      query: (id) => ({
        url: `v2/stations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [STATIONS_INVALIDATE_TAG], // TODO: govnishe (переделать на удаление из кеша именно удаленной станции)
    }),

    // получение паспорта
    getStationEntity: builder.query({
      query: (id) => ({
        url: `station/${id}/entities`,
        method: 'get',
      }),
      providesTags: [STATIONS_INVALIDATE_TAG],
      // // Устанавливаем теги по которым инвалидируем кеш (теги регестрируются в dcpc)
      // providesTags: ({ id }) => {
      //     return [{ type: "Station", id }];
      // },
    }),

    // Получение сообщений
    getStationMessages: builder.query({
      query: ({ params, id }) => ({
        url: `messagesout/station/${id}`,
        params,
      }),
    }),

    // Работа с каналами
    getStationChannels: builder.query({
      query: (stationId) => `station/${stationId}/channels`,
      providesTags: [STATION_CHANNELS_INVALIDATE_TAG],
    }),

    updateStationChannels: builder.mutation({
      query: ({ id, body }) => ({
        url: `station/${id}/channels`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [STATION_CHANNELS_INVALIDATE_TAG],
    }),

    // Телеграммы станции
    getStationTelegrams: builder.query({
      query: ({ stationId, telegramTypeId, params }) => ({
        url: `station/${stationId}/telegrams/${telegramTypeId}`,
        params,
      }),
    }),

    postStationTS: builder.mutation({
      query: (data) => ({
        url: '/station-ts',
        method: 'POST',
        body: data,
      }),
    }),

    pullStationTS: builder.mutation({
      query: (body) => ({
        // url: `/station-ts/station/${args.stationId}/time/${args.startDate}/${args.endDate}/meteocodes/${args.meteoCodeIds}/vocmodes/${args.vocmodeIds}/graph`,
        url: 'dv-time-series/charts',
        method: 'POST',
        body,
      }),
    }),

    stationFormingCsv: builder.query({
      query: (args) => ({
        method: 'GET',
        url: `station-ts/csv?${args}`,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          toaster.danger('Произошла ошибка при формировании CSV');
        }
      },
    }),
    getStationsWithDatasourceProgramBlocks: builder.query({
      query: (params) => ({
        url: 'stations/datasource-program-blocks',
        params,
      }),
    }),
  }),
});

// console.log(stationsApi, '\n\n');

export const {
  useGetStationsQuery,
  useUpdateStationMutation,
  useDeleteStationMutation,
  useGetDatasourceByStationQuery,

  useGetStationEntityQuery,
  useLazyGetStationEntityQuery,

  useLazyGetStationMessagesQuery,

  useGetStationChannelsQuery,
  useUpdateStationChannelsMutation,

  useGetStationTelegramsQuery,
  useLazyGetStationTelegramsQuery,

  usePostStationTSMutation,
  usePullStationTSMutation,

  useLazyStationFormingCsvQuery,

  useLazyGetStationsWithDatasourceProgramBlocksQuery,
} = adapterForApi(stationsApi);

// TODO: проверить где это используется, изменить, удалить
export const stationsSelect = stationsApi.endpoints.getStations.select();
export const stationsSelector = (state) => stationsSelect(state).data || [];
export const stationSelect = (state, stationId) => stationsSelector(state).find((station) => station.id ===  parseInt(stationId, 10));
export const stationSelector = (state, id) => {
  return stationSelect(state, id);
};

// todo: вынести в источники ???
const datasourceByStationSelector = (state, stationId, datasourceId) => {
  const station = stationSelector(state, stationId);
  if (station) {
    const datasource = station?.datasources?.find((ds) => ds?.id === parseInt(datasourceId, 10));
    if (datasource) {
      return datasource;
    }
  }
  return undefined;
};

export const datasourcesByStationSelector = (state, stationId, excludeTypeIds = []) => {
  const station = stationSelector(state, stationId);
  if (station) {
    if (excludeTypeIds.length > 0) return station.datasources.filter((datasource) => !excludeTypeIds.includes(datasource.datasourceType.id));
    return station.datasources;
  }
  return [];
};

// getStations
export const invalidateStations = () => store.dispatch(invalidateTags([STATIONS_INVALIDATE_TAG]));
export const wsReloadStation = {
  reload: (id) => {
    if (stationsSelect(store.getState()).isSuccess) {
      store.dispatch(stationsApi.endpoints.getStationByID.initiate(id, {
        subscribe: false,
        forceRefetch: true,
      }));
    }
  },
  type: 'Station',
};

// todo new
// профиль для станции
const createProfileByStationIdSelector = createSelector(
  (state, stationId) => stationSelector(state, stationId),
  (station) => {
    const initialProfile = [
      {
        idx: 'Синоптический индекс',
      },
      {
        'region.name': 'ЦГМС',
      },
      {
        'region.vocRegionType.name': 'Принадлежность',
      },
      {
        oscar: 'Уникальный идентификатор',
      },
      {
        name: 'Название станции',
      },
      {
        alias: 'Алиас',
      },
      {
        'vocStationType.vocStationCat.name': 'Категория станции',
      },
      {
        'vocStationType.name': 'Тип станции',
      },
      {
        csquares: 'С-squares',
      },
      {
        altitude: 'Высота',
      },
      {
        longitude: 'Долгота',
      },
      {
        latitude: 'Широта',
      },
    ];
    return initialProfile.reduce((acc, profileField) => {
      const value = getPath(station, Object.keys(profileField)[0]);
      if (!value && Object.keys(profileField)[0] !== 'oscar' && Object.keys(profileField)[0] !== 'csquares') return acc;
      acc.push({ [profileField[Object.keys(profileField)[0]]]: value });
      return acc;
    }, []);
  },
);
export const profileByStationIdSelector = (stationId) => (state) => createProfileByStationIdSelector(state, stationId);

// источник по станции
export const datasourceByStationIdAndDatasourceIdSelector = (stationId, datasourceId) => (state) => datasourceByStationSelector(state, stationId, datasourceId);
// профиль источника по станции
export const datasourceProfileByStationIdAndDatasourceIdSelector = (stationId, datasourceId) => (state) => {
  const datasource = datasourceByStationSelector(state, stationId, datasourceId);
  const initialProfile = [
    { // тут нужна ф-ция, потому что вместо true/false отображается "ничего"
      enabled: (value) => ({
        Включен: value ? 'вкл' : 'выкл',
      }),
    },
    {
      transit: (value) => ({
        Транзит: value ? 'вкл' : 'выкл',
      }),
    },
    {
      'datasourceStatus.status': 'Статус',
    },
    {
      'vocMode.name': 'Режим',
    },
    {
      serialNum: 'Серийный номер',
    },
    {
      'datasourceType.fullName': 'Название',
    },
    {
      'datasourceType.shortName': 'Короткое название',
    },
    {
      'datasourceType.dataSourceCat.fullName': 'Название категории',
    },
    {
      'datasourceType.dataSourceCat.shortName': 'Короткое название категории',
    },
    { // и тут тоже, потому что значение incomming/outgoing
      'datasourceType.dataSourceCat.datasourceDirection.name': (value) => ({
        'Тип источника входящие/исходящие': value === 'incomming' ? 'входящие' : 'исходящие',
      }),
    },
    {
      phoneNumber: 'Номер телефона',
    },
  ];
  return initialProfile.reduce((acc, profileField) => {
    const value = getPath(datasource, Object.keys(profileField)[0]);
    if (!value && typeof profileField[Object.keys(profileField)[0]] !== 'function') return acc;
    if (typeof profileField[Object.keys(profileField)[0]] === 'function') {
      acc.push(profileField[Object.keys(profileField)[0]](value));
    } else acc.push({ [profileField[Object.keys(profileField)[0]]]: value });
    return acc;
  }, []);
};

// todo !delete когда исправят в Report.Form.Datasources.js(ReportsSelectedDatasources)
export const reportsStationsBySelectedReportFilter = (selectedReport) => createAdapterFilter((stations) => {
  if (!selectedReport?.report) return [];
  const stationsKeys = Object.keys(selectedReport.report.datasources).map((stationKey) => parseInt(stationKey, 10));
  if (!stationsKeys?.length) return [];
  return stations
    .filter((station) => stationsKeys.includes(station.id))
    .reduce((acc, station) => {
      if (station) {
        const copyStation = { ...station };
        if (copyStation.datasources) {
          copyStation.datasources = copyStation.datasources
            .filter((datasource) => selectedReport.report.datasources[station.id].includes(datasource.id));
        }
        acc.push(copyStation);
      }
      return acc;
    }, []);
});

export const updateStation = (station) => stationsApi.util.updateQueryData('getStations', undefined, (stations) => {
  const a = {};
  const findIndex = stations.findIndex((el) => el.id === station.id);
  const key = findIndex !== -1 ? findIndex : stations.length;
  a[key] = station;
  Object.assign(stations, a);
});
